import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Select, SelectOption } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from './ui/table';
import { Spinner } from './ui/spinner';
import { useToast } from './ui/toast';
import { Plus, X } from 'lucide-react';
import api from '../services/api';

const Drivers = () => {
  const [drivers, setDrivers] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({});
  const [assignments, setAssignments] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const { toast } = useToast();

  const fetchDrivers = async () => {
    setLoading(true);
    try { const res = await api.get('/drivers'); setDrivers(res.data); }
    catch { toast({ title: 'Хүргэлтийн ажилтнуудыг татахад алдаа гарлаа', variant: 'error' }); }
    setLoading(false);
  };

  const fetchDistricts = async () => {
    try { const res = await api.get('/districts'); setDistricts(res.data); } catch {}
  };

  useEffect(() => { fetchDrivers(); fetchDistricts(); }, []);

  const handleSubmit = async () => {
    try {
      const data = {
        ...form,
        districtAssignments: assignments.map(a => ({ district: a.districtId, khoroos: a.allKhoroos ? [] : a.khoroos, allKhoroos: a.allKhoroos || false })),
        districts: assignments.map(a => a.districtId),
      };
      if (editingId) {
        if (!data.password) delete data.password;
        await api.put(`/drivers/${editingId}`, data);
        toast({ title: 'Хүргэлтийн ажилтан шинэчлэгдлээ', variant: 'success' });
      } else {
        await api.post('/drivers', data);
        toast({ title: 'Хүргэлтийн ажилтан нэмэгдлээ', variant: 'success' });
      }
      setModalOpen(false);
      setForm({});
      setAssignments([]);
      setEditingId(null);
      fetchDrivers();
    } catch (error) {
      toast({ title: error.response?.data?.message || 'Алдаа гарлаа', variant: 'error' });
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/drivers/${id}`);
      toast({ title: 'Устгагдлаа', variant: 'success' });
      setDeleteConfirm(null);
      fetchDrivers();
    } catch {
      toast({ title: 'Устгахад алдаа гарлаа', variant: 'error' });
    }
  };

  const openEdit = (driver) => {
    setEditingId(driver._id);
    setForm({ name: driver.name, phone: driver.phone, vehicleNumber: driver.vehicleNumber });
    const existing = (driver.districtAssignments || []).map(a => ({
      districtId: a.district?._id || a.district,
      khoroos: a.khoroos || [],
      allKhoroos: a.allKhoroos || false,
    }));
    if (existing.length === 0 && driver.districts?.length > 0) {
      setAssignments(driver.districts.map(d => ({ districtId: d._id, khoroos: [] })));
    } else {
      setAssignments(existing);
    }
    setModalOpen(true);
  };

  const addDistrict = (districtId) => {
    if (!districtId || assignments.find(a => a.districtId === districtId)) return;
    setAssignments(prev => [...prev, { districtId, khoroos: [], allKhoroos: false }]);
  };

  const removeDistrict = (districtId) => {
    setAssignments(prev => prev.filter(a => a.districtId !== districtId));
  };

  const toggleKhoroo = (districtId, khoroo) => {
    setAssignments(prev => prev.map(a => {
      if (a.districtId !== districtId) return a;
      const has = a.khoroos.includes(khoroo);
      return { ...a, khoroos: has ? a.khoroos.filter(k => k !== khoroo) : [...a.khoroos, khoroo].sort((x, y) => x - y) };
    }));
  };

  const selectAllKhoroos = (districtId) => {
    const dist = districts.find(d => d._id === districtId);
    if (!dist) return;
    setAssignments(prev => prev.map(a => {
      if (a.districtId !== districtId) return a;
      return { ...a, khoroos: [...(dist.khoroos || [])] };
    }));
  };

  const getDistrictName = (id) => districts.find(d => d._id === id)?.name || '';
  const getDistrictKhoroos = (id) => districts.find(d => d._id === id)?.khoroos || [];
  const availableDistricts = districts.filter(d => d.isActive && !assignments.find(a => a.districtId === d._id));

  const formatAssignments = (driver) => {
    const da = driver.districtAssignments || [];
    if (da.length > 0) {
      return da.map(a => {
        const name = a.district?.name || '';
        if (a.allKhoroos) return `${name} (бүх хороо)`;
        if (a.khoroos?.length > 0) return `${name} (${a.khoroos.join(',')}-р хороо)`;
        return name;
      }).join(', ');
    }
    return driver.districts?.map(d => d.name).join(', ') || '-';
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold">Хүргэлтийн ажилтнууд</h2>
        <Button onClick={() => { setEditingId(null); setForm({}); setAssignments([]); setModalOpen(true); }}>
          <Plus className="h-4 w-4 mr-2" />Хүргэлтийн ажилтан нэмэх
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Spinner /></div>
      ) : (
        <div className="border rounded-lg overflow-x-auto bg-white">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Нэр</TableHead>
                <TableHead>Утас</TableHead>
                <TableHead>Машин №</TableHead>
                <TableHead>Дүүрэг / Хороо</TableHead>
                <TableHead>Төлөв</TableHead>
                <TableHead>Үйлдэл</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {drivers.map(d => (
                <TableRow key={d._id}>
                  <TableCell className="font-medium">{d.name}</TableCell>
                  <TableCell>{d.phone}</TableCell>
                  <TableCell>{d.vehicleNumber}</TableCell>
                  <TableCell className="max-w-[300px]">
                    <span className="text-sm">{formatAssignments(d)}</span>
                  </TableCell>
                  <TableCell><Badge variant={d.isActive ? 'success' : 'destructive'}>{d.isActive ? 'Идэвхтэй' : 'Идэвхгүй'}</Badge></TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => openEdit(d)}>Засах</Button>
                      <Button size="sm" variant="destructive" onClick={() => setDeleteConfirm(d._id)}>Устгах</Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {drivers.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-zinc-400">Хүргэлтийн ажилтан байхгүй</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={modalOpen} onOpenChange={(v) => { if (!v) { setModalOpen(false); setEditingId(null); setForm({}); setAssignments([]); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editingId ? 'Хүргэлтийн ажилтан засах' : 'Хүргэлтийн ажилтан нэмэх'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-sm font-medium">Нэр *</label>
              <Input value={form.name || ''} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></div>
            <div><label className="text-sm font-medium">Утас *</label>
              <Input value={form.phone || ''} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} /></div>
            <div><label className="text-sm font-medium">{editingId ? 'Нууц үг (хоосон = өөрчлөхгүй)' : 'Нууц үг *'}</label>
              <Input type="password" value={form.password || ''} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} /></div>
            <div><label className="text-sm font-medium">Машины дугаар</label>
              <Input value={form.vehicleNumber || ''} onChange={e => setForm(f => ({ ...f, vehicleNumber: e.target.value }))} /></div>

            <div>
              <label className="text-sm font-medium">Дүүрэг / Хороо</label>
              {availableDistricts.length > 0 && (
                <Select value="" onChange={e => { addDistrict(e.target.value); }} className="mt-1">
                  <SelectOption value="">+ Дүүрэг нэмэх</SelectOption>
                  {availableDistricts.map(d => <SelectOption key={d._id} value={d._id}>{d.name}</SelectOption>)}
                </Select>
              )}

              {assignments.length > 0 && (
                <div className="space-y-2 mt-2">
                  {assignments.map(a => {
                    const khoroos = getDistrictKhoroos(a.districtId);
                    return (
                      <div key={a.districtId} className="border rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-sm">{getDistrictName(a.districtId)}</span>
                          <button onClick={() => removeDistrict(a.districtId)} className="text-red-500 hover:text-red-700">
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                        <div className="mb-2">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={a.allKhoroos || false}
                              onChange={e => setAssignments(prev => prev.map(x => x.districtId === a.districtId ? { ...x, allKhoroos: e.target.checked } : x))}
                              className="rounded" />
                            <span className="text-xs font-medium text-zinc-600">Хороо хамаагүй (бүх захиалга)</span>
                          </label>
                        </div>
                        {!a.allKhoroos && khoroos.length > 0 && (
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs text-zinc-500">Хороод:</span>
                              <button className="text-xs text-primary hover:underline"
                                onClick={() => selectAllKhoroos(a.districtId)}>Бүгдийг сонгох</button>
                              <button className="text-xs text-zinc-400 hover:underline"
                                onClick={() => setAssignments(prev => prev.map(x => x.districtId === a.districtId ? { ...x, khoroos: [] } : x))}>Цэвэрлэх</button>
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {khoroos.map(k => (
                                <button key={k} onClick={() => toggleKhoroo(a.districtId, k)}
                                  className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                                    a.khoroos.includes(k)
                                      ? 'bg-primary text-white'
                                      : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                                  }`}>
                                  {k}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setModalOpen(false); setEditingId(null); setForm({}); setAssignments([]); }}>Болих</Button>
            <Button onClick={handleSubmit}>{editingId ? 'Хадгалах' : 'Нэмэх'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteConfirm} onOpenChange={(v) => !v && setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Устгах уу?</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-600">Энэ жолоочийг устгахдаа итгэлтэй байна уу?</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Болих</Button>
            <Button variant="destructive" onClick={() => handleDelete(deleteConfirm)}>Устгах</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Drivers;
